import React from 'react';

/**
 * The small pieces the Care Planner's screens share.
 *
 * Everything clock-dependent — overdue, due now, late, needs review, needs
 * allocation — arrives on each task from the API (utils/carePlanner.js).
 * Nothing here recalculates it; these helpers only turn what came back into
 * words and colours, so the grid, the task panel and the export cannot
 * describe the same task differently.
 */

/* ------------------------------------------------------------- dates - */

// Midday, so a DST change can never push a date over a boundary.
const at = (ymd) => new Date(`${ymd}T12:00:00`);
const iso = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const localToday = () => iso(new Date());

export const addDays = (ymd, days) => {
  const d = at(ymd);
  d.setDate(d.getDate() + days);
  return iso(d);
};

/** The Monday of the week containing `ymd`. */
export const mondayOf = (ymd) => {
  const d = at(ymd);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return iso(d);
};

/** The dates a view covers: one day, or Monday–Sunday. */
export const datesFor = (view, ymd) => {
  if (view !== 'week') return [ymd];
  const monday = mondayOf(ymd);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
};

export const dayLabel = (ymd) => at(ymd).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
export const longDayLabel = (ymd) => at(ymd).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
export const shortDayLabel = (ymd) => at(ymd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
export const weekdayShort = (ymd) => at(ymd).toLocaleDateString('en-GB', { weekday: 'short' });

export const clock = (value) =>
  (value ? new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : null);
export const stamp = (value) =>
  (value ? new Date(value).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : null);

/** JavaScript weekday numbers (0 = Sunday), presented Monday first. */
export const WEEKDAYS = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' }, { value: 0, label: 'Sun' },
];

/* ------------------------------------------------------------- times - */

export const toMinutes = (hm) => {
  const [h, m] = String(hm || '').split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? (h * 60) + m : null;
};
export const fromMinutes = (total) =>
  `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;

export const DEFAULT_PERIODS = [
  { key: 'morning', label: 'Morning', start: 360, end: 720 },
  { key: 'afternoon', label: 'Afternoon', start: 720, end: 1020 },
  { key: 'evening', label: 'Evening', start: 1020, end: 1260 },
  { key: 'night', label: 'Night', start: 1260, end: 360 },
];

/** Which time-of-day column a start time falls in. Night wraps midnight. */
export const periodOf = (hm, periods = DEFAULT_PERIODS) => {
  const minutes = toMinutes(hm) ?? 0;
  const hit = periods.find((p) => (p.start < p.end
    ? minutes >= p.start && minutes < p.end
    : minutes >= p.start || minutes < p.end));
  return (hit || periods[periods.length - 1]).key;
};

/* ------------------------------------------------------------ people - */

// Words that start with a letter only, so "Margaret (Oak resident)" is "MO", not "M(".
export const initials = (name) =>
  (name || '').trim().split(/\s+/).filter((p) => /^\p{L}/u.test(p)).slice(0, 2)
    .map((p) => p[0].toUpperCase()).join('') || '?';

export const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

export const carerNames = (task) => [task.assigned_name, task.second_name].filter(Boolean).join(' + ');

/* ------------------------------------------------------------- state - */

/**
 * The card colour, in the order a manager needs to see it: a confirmed miss
 * first, then what was recorded, then what still needs a carer, then what is
 * running late, and finally ordinary allocated care.
 */
export const toneOf = (task) => {
  if (task.state === 'missed') return 'missed';
  if (task.state === 'completed') return 'completed';
  if (task.state === 'not_done') return 'not_done';
  if (task.needs_allocation) return 'unassigned';
  if (task.overdue) return 'overdue';
  return 'assigned';
};

export const CARD_TONES = {
  missed: 'border-[#FECACA] border-l-[#DC2626] bg-[#FEF2F2] text-[#B91C1C] dark:bg-rose-950/40 dark:border-rose-900/60 dark:border-l-rose-500 dark:text-rose-300',
  completed: 'border-[#A7F3D0] border-l-[#10B981] bg-[#ECFDF5] text-[#047857] dark:bg-emerald-950/40 dark:border-emerald-900/60 dark:border-l-emerald-500 dark:text-emerald-300',
  not_done: 'border-[#FDE68A] border-l-[#D97706] bg-[#FFFBEB] text-[#92400E] dark:bg-amber-950/40 dark:border-amber-900/60 dark:border-l-amber-500 dark:text-amber-300',
  overdue: 'border-[#FDE68A] border-l-[#F59E0B] bg-[#FFFBEB] text-[#92400E] dark:bg-amber-950/40 dark:border-amber-900/60 dark:border-l-amber-500 dark:text-amber-300',
  unassigned: 'border-[#E4D4FF] border-l-[#9747ED] bg-[#FAF5FF] text-[#7E22CE] dark:bg-purple-950/40 dark:border-purple-900/60 dark:border-l-purple-500 dark:text-purple-300',
  assigned: 'border-[#C9DBFF] border-l-[#2563EB] bg-[#EFF5FF] text-[#1E40AF] dark:bg-blue-950/40 dark:border-blue-900/60 dark:border-l-blue-500 dark:text-blue-300',
};

export const COLOUR_KEY = [
  { tone: 'assigned', label: 'Assigned', text: 'Allocated to a named carer.' },
  { tone: 'unassigned', label: 'Unassigned', text: 'Still needs a carer — or a second carer for two-person care.' },
  { tone: 'overdue', label: 'Overdue / not done', text: 'The window has passed with nothing recorded, or the carer recorded it as not done.' },
  { tone: 'completed', label: 'Completed', text: 'Recorded as given by the carer in My Shift.' },
  { tone: 'missed', label: 'Missed', text: 'A manager confirmed it did not happen, with a reason.' },
];

/* ------------------------------------------------------------ visits - */

/**
 * Everything planned together for one resident at one time is ONE care visit:
 * "14:00 Iris — Repositioning, Washing & bathing, Hearing aid support" is one
 * card on the planner, one item in the carer's My Shift, and one care note
 * when it is recorded. The tasks stay separate underneath (each has its own
 * outcome), so the visit is only a grouping: same plan, same day, same start.
 */
export const visitKey = (task) => `${task.plan_id}|${task.task_date}|${task.start_time}`;

const VISIT_TONE_ORDER = ['missed', 'unassigned', 'overdue', 'not_done', 'assigned', 'completed'];

export const toVisit = (key, tasks) => {
  const list = [...tasks].sort((a, b) => a.id - b.id);
  const recorded = list.filter((t) => !t.open);
  const open = list.filter((t) => t.open);
  const tones = list.map(toneOf);
  // A visit reads as its most pressing task; fully recorded, as done or not.
  const tone = open.length
    ? VISIT_TONE_ORDER.find((t) => tones.includes(t) && t !== 'completed' && t !== 'not_done') || 'assigned'
    : (tones.includes('missed') ? 'missed' : tones.every((t) => t === 'completed') ? 'completed' : 'not_done');
  const first = list[0];
  return {
    key,
    tasks: list,
    first,
    labels: list.map((t) => t.action_label),
    open,
    recorded,
    tone,
    needs_review: list.some((t) => t.needs_review),
    needs_allocation: open.some((t) => t.needs_allocation),
    late: list.some((t) => t.late),
    priority: list.some((t) => t.priority === 'urgent') ? 'urgent' : list.some((t) => t.priority === 'important') ? 'important' : 'routine',
  };
};

export const groupVisits = (tasks) => {
  const map = new Map();
  tasks.forEach((t) => {
    const key = visitKey(t);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(t);
  });
  return [...map.entries()].map(([key, list]) => toVisit(key, list));
};

/** The status line of a visit card: one task's status, or "2 of 4 done". */
export const visitStatus = (visit) => {
  if (visit.tasks.length === 1) return statusLabel(visit.first);
  if (!visit.open.length) {
    if (visit.tone === 'missed') return 'Missed';
    return visit.tone === 'completed' ? 'All done' : `${visit.recorded.length} recorded`;
  }
  if (visit.recorded.length) return `${visit.recorded.length} of ${visit.tasks.length} done`;
  return statusLabel(visit.open[0]);
};

/** The one-word status shown on a card, or null for ordinary scheduled care. */
export const statusLabel = (task) => {
  if (task.state === 'missed') return 'Missed';
  if (task.state === 'completed' || task.state === 'not_done') return task.outcome_label || 'Recorded';
  if (task.state === 'in_progress') return 'In progress';
  if (task.state === 'overdue') return 'Overdue';
  if (task.state === 'due_now') return 'Due now';
  return null;
};

const BADGE_TONES = {
  gray: 'bg-[#F1F5F9] text-[#64748B] dark:bg-gray-800 dark:text-gray-400',
  blue: 'bg-[#EEF3FF] text-blue-600 dark:bg-blue-900/20 dark:text-blue-300',
  green: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400',
  amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  red: 'bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400',
  purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300',
};

export const Badge = ({ tone = 'gray', children, className = '' }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-[3px] rounded-md whitespace-nowrap ${BADGE_TONES[tone] || BADGE_TONES.gray} ${className}`}>
    {children}
  </span>
);

export const stateBadge = (task) => ({
  missed: { tone: 'red', label: 'Missed' },
  completed: { tone: 'green', label: task.outcome_label || 'Completed' },
  not_done: { tone: 'amber', label: task.outcome_label || 'Not done' },
  in_progress: { tone: 'blue', label: 'In progress' },
  overdue: { tone: 'amber', label: 'Overdue' },
  due_now: { tone: 'blue', label: 'Due now' },
  scheduled: { tone: 'gray', label: 'Scheduled' },
}[task.state] || { tone: 'gray', label: 'Scheduled' });

export const PRIORITY_BADGE = {
  urgent: { tone: 'red', label: 'Urgent' },
  important: { tone: 'amber', label: 'Important' },
};

/* -------------------------------------------------------------- rota - */

/** "Working 07:00–15:00", "Off duty", "Annual leave", or null when there is no rota on record. */
export const rotaLabel = (entry) => {
  if (!entry) return null;
  if (entry.absence && !entry.absence.from) return entry.absence.reason || 'On leave';
  if (!entry.on) return 'Off duty';
  return `${entry.start}–${entry.end}`;
};

/**
 * Is this carer rostered for this care? Only answers for dates whose rota is
 * loaded; returns a sentence to show, or null. Advisory only — the rota is
 * often incomplete, so the planner warns rather than refusing.
 */
export const rotaWarning = (rota, carer, dates, start, end) => {
  if (!carer || !rota) return null;
  const byDate = rota[carer.id];
  if (!byDate) return null;
  const s = toMinutes(start);
  const e = toMinutes(end);
  for (const date of dates) {
    const entry = byDate[date];
    if (entry === undefined || entry === null) continue;
    if (entry.absence && !entry.absence.from) return `${carer.name} is on ${String(entry.absence.reason || 'leave').toLowerCase()} on ${dayLabel(date)}.`;
    if (!entry.on) return `${carer.name} is not rostered on ${dayLabel(date)}.`;
    const rs = toMinutes(entry.start);
    const re = toMinutes(entry.end);
    if (s === null || e === null || rs === null || re === null) continue;
    const overnight = re <= rs;
    const inside = overnight ? (s >= rs || e <= re) : (s >= rs && e <= re);
    if (!inside) return `${carer.name} works ${entry.start}–${entry.end} on ${dayLabel(date)}, outside this time.`;
  }
  return null;
};

/* ------------------------------------------------------------ export - */

const cell = (value) => {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const exportCsv = (tasks, filename) => {
  const header = ['Date', 'Start', 'End', 'Resident', 'Room', 'Category', 'Care task', 'Priority', 'Staff required',
    'Carer', 'Second carer', 'Status', 'Outcome', 'Reason', 'Notes', 'Recorded by', 'Recorded at', 'Missed reason', 'Reviewed by'];
  const rows = tasks.map((t) => [
    t.task_date, t.start_time, t.end_time, t.resident?.name, t.resident?.room, t.category, t.action_label, t.priority,
    t.staff_required, t.assigned_name || 'Unassigned', t.second_name, stateBadge(t).label, t.outcome_label, t.outcome_reason,
    t.outcome_notes, t.recorded_by_name, stamp(t.completed_at), t.missed_reason, t.reviewed_by_name,
  ]);
  const csv = [header, ...rows].map((r) => r.map(cell).join(',')).join('\r\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/* ---------------------------------------------------------------- UI - */

export const CARD = 'bg-white dark:bg-[#262b33] rounded-[16px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.04)]';

export const inputClass =
  'w-full h-10 border border-[#DCE4EF] dark:border-gray-700 rounded-xl px-3 text-[13.5px] text-[#0B1220] dark:text-gray-100 placeholder-[#A0AEC0] dark:placeholder-gray-600 bg-white dark:bg-[#1f232a] focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors disabled:bg-[#F5F7FA] disabled:text-[#94A3B8] dark:disabled:bg-[#20242c]';

export const FieldLabel = ({ children, htmlFor, required, hint }) => (
  <div className="flex items-baseline justify-between gap-3 mb-1.5">
    <label htmlFor={htmlFor} className="text-[12px] font-semibold text-[#334155] dark:text-gray-300">
      {children} {required && <span className="text-rose-500">*</span>}
    </label>
    {hint && <span className="text-[11px] text-[#94A3B8] dark:text-gray-500">{hint}</span>}
  </div>
);

/** A two-or-more option toggle, the same shape as the quality module's tabs. */
export const Segmented = ({ options, value, onChange, size = 'md', label }) => (
  <div role="group" aria-label={label} className="inline-flex gap-1 p-1 bg-[#E7EDF5] dark:bg-gray-800 border border-[#DCE5F1] dark:border-gray-700 rounded-[11px]">
    {options.map((o) => {
      const active = o.value === value;
      return (
        <button
          key={o.value}
          type="button"
          aria-pressed={active}
          onClick={() => onChange(o.value)}
          className={`inline-flex items-center gap-1.5 px-3.5 ${size === 'sm' ? 'h-8' : 'h-9'} rounded-[7px] text-[12.5px] font-semibold transition-colors ${
            active
              ? 'bg-white dark:bg-[#262b33] text-blue-600 dark:text-blue-300 shadow-[0_1px_4px_rgba(24,55,100,0.08)]'
              : 'text-[#64738B] dark:text-gray-400 hover:text-[#0B1220] dark:hover:text-gray-200'
          }`}
        >
          {o.icon}
          {o.label}
        </button>
      );
    })}
  </div>
);

/**
 * Pull the human message out of an axios failure. The API always sends one,
 * written for the manager ("Margaret already has Fluid intake at 10:00 on …").
 */
export const errorMessage = (error, fallback = 'Something went wrong. Please try again.') =>
  error?.response?.data?.message || error?.message || fallback;
